import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, ChevronLeft, Layers, DoorOpen, UserRound } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ymd, meseLabel, last12Months, DOW_ABBR, METODO_COLOR, freqLabel, groupBy, fmtGiornoLong } from "./helpers";

export default function PuliziePianificazione({ structureId, operatori = [] }) {
  const qc = useQueryClient();
  const [mese, setMese] = useState(new Date().toISOString().slice(0, 7));
  const [openDay, setOpenDay] = useState(null);
  const [dlg, setDlg] = useState(null);
  const months = last12Months();
  const todayIso = ymd(new Date());

  const { data: mData, isLoading: mLoading } = useQuery({
    queryKey: ["pul-pian-mese", structureId, mese],
    queryFn: async () => (await api.get("/pulizie/pian-mese", { params: { structure_id: structureId, mese } })).data,
  });
  const { data: week = {}, isLoading: dLoading } = useQuery({
    enabled: !!openDay,
    queryKey: ["pul-week", structureId, openDay],
    queryFn: async () => (await api.get("/pulizie/settimana", { params: { structure_id: structureId, dal: openDay } })).data,
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["pul-week"] }); qc.invalidateQueries({ queryKey: ["pul-pian-mese"] }); qc.invalidateQueries({ queryKey: ["pul-checklist"] }); qc.invalidateQueries({ queryKey: ["pul-mese"] }); };

  const reassign = async (item, opId) => {
    const op = operatori.find((o) => o.id === opId);
    try {
      await api.post("/pulizie/piano-assegna", { structure_id: structureId, data: openDay, attivita_id: item.attivita_id, operatore_id: opId === "none" ? null : opId, operatore_nome: op ? `${op.nome} ${op.cognome}`.trim() : null });
      toast.success(op ? `Assegnata a ${op.nome} ${op.cognome}` : "Assegnazione rimossa");
      refresh();
    } catch { toast.error("Errore"); }
  };

  // ---- Vista dettaglio giorno ----
  if (openDay) {
    const items = (week[openDay] || []).slice().sort((a, b) => (a.luogo || "").localeCompare(b.luogo || "", "it") || (a.descrizione || "").localeCompare(b.descrizione || "", "it"));
    const pianGroups = groupBy(items.map((it) => ({ ...it, area_nome: it.area_nome || it.luogo, stanza_nome: it.stanza_nome })));
    const assegnate = items.filter((i) => i.assegnato_id).length;
    const opsCoinvolti = new Set(items.filter((i) => i.assegnato_id).map((i) => i.assegnato_id)).size;
    return (
      <div data-testid="pul-pian-day-detail">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Button variant="outline" size="sm" onClick={() => setOpenDay(null)} data-testid="pul-pian-back"><ChevronLeft size={15} className="mr-1" /> Torna al mese</Button>
          <h3 className="font-head font-bold text-base flex items-center gap-2"><Layers size={16} className="text-sky-600" /> {fmtGiornoLong(openDay)}</h3>
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => setDlg({ scope: "giorno", data: openDay })} data-testid="pul-assign-day-auto"><UserRound size={14} className="mr-1" /> Assegna automaticamente</Button>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white rounded-xl border p-3" data-testid="pul-pian-tot"><div className="text-[11px] text-muted-foreground font-semibold uppercase">Pulizie del giorno</div><div className="text-2xl font-head font-bold">{items.length}</div></div>
          <div className="bg-white rounded-xl border p-3" data-testid="pul-pian-ass"><div className="text-[11px] text-muted-foreground font-semibold uppercase">Assegnate</div><div className="text-2xl font-head font-bold text-emerald-700">{assegnate}</div><div className="text-[10px] text-muted-foreground">{opsCoinvolti} operatori</div></div>
          <div className="bg-white rounded-xl border p-3" data-testid="pul-pian-daass"><div className="text-[11px] text-muted-foreground font-semibold uppercase">Da assegnare</div><div className={`text-2xl font-head font-bold ${items.length - assegnate > 0 ? "text-amber-600" : "text-slate-400"}`}>{items.length - assegnate}</div></div>
        </div>
        {dLoading ? <p className="text-muted-foreground">Caricamento…</p> : items.length === 0 ? (
          <div className="text-center py-14 text-muted-foreground bg-white rounded-xl border"><Sparkles className="mx-auto mb-3 opacity-40" size={36} /><p>Nessuna pulizia programmata per questo giorno.</p></div>
        ) : (
          <div className="space-y-5">
            {Object.entries(pianGroups).map(([area, stanze]) => (
              <div key={area} className="bg-white rounded-xl border overflow-hidden">
                <div className="px-4 py-2.5 bg-slate-50 border-b font-bold flex items-center gap-2"><Layers size={15} className="text-slate-500" /> {area}</div>
                <div className="divide-y">
                  {Object.entries(stanze).map(([stanza, its]) => (
                    <div key={stanza} className="p-3">
                      {stanza !== "__area__" && <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><DoorOpen size={13} /> {stanza}</div>}
                      <div className="space-y-1.5">
                        {its.map((it) => (
                          <div key={it.attivita_id} className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-slate-50" data-testid={`pul-pian-row-${it.attivita_id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium">{it.descrizione}{it.elemento_nome ? <span className="text-muted-foreground font-normal"> · {it.elemento_nome}</span> : ""}</div>
                              <div className="flex items-center gap-2 mt-0.5">
                                {it.metodo && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full text-white" style={{ background: METODO_COLOR[it.metodo] || "#64748b" }}>{it.metodo}</span>}
                                <span className="text-[10px] text-muted-foreground">{freqLabel(it)}</span>
                              </div>
                            </div>
                            <div className="shrink-0 w-[190px]">
                              <Select value={it.assegnato_id || "none"} onValueChange={(v) => reassign(it, v)}>
                                <SelectTrigger className={`h-8 text-sm ${it.assegnato_id ? "" : "text-muted-foreground"}`} data-testid={`pul-pian-assign-${it.attivita_id}`}><SelectValue placeholder="Non assegnata" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Non assegnata</SelectItem>
                                  {operatori.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome} {o.cognome}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {dlg && <AssegnaDialog dlg={dlg} structureId={structureId} operatori={operatori} onClose={() => setDlg(null)} onSaved={refresh} />}
      </div>
    );
  }

  // ---- Vista mese (mesi -> box -> giorni) ----
  const giorni = mData?.giorni || [];
  const tot = mData?.totali || {};
  return (
    <div data-testid="pul-pian">
      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
        {months.map((m) => (
          <button key={m} onClick={() => setMese(m)} data-testid={`pul-pian-mese-${m}`} className={`px-3.5 py-2 rounded-xl text-sm font-semibold whitespace-nowrap shrink-0 border transition-colors capitalize ${mese === m ? "bg-sky-600 text-white border-sky-600" : "bg-white hover:bg-slate-50"}`}>{meseLabel(m)}</button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-white rounded-xl border p-4" data-testid="pul-pian-m-tot"><div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Pulizie previste</div><div className="text-3xl font-head font-bold">{tot.totale || 0}</div><div className="text-[11px] text-muted-foreground">nel mese</div></div>
        <div className="bg-white rounded-xl border p-4" data-testid="pul-pian-m-ass"><div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Assegnate</div><div className="text-3xl font-head font-bold text-emerald-700">{tot.assegnate || 0}</div></div>
        <div className="bg-white rounded-xl border p-4" data-testid="pul-pian-m-daass"><div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Da assegnare</div><div className={`text-3xl font-head font-bold ${(tot.da_assegnare || 0) > 0 ? "text-amber-600" : "text-slate-400"}`}>{tot.da_assegnare || 0}</div></div>
      </div>

      <h3 className="font-head font-bold text-base mb-3 flex items-center gap-2"><Layers size={16} className="text-sky-600" /> Giorni di <span className="capitalize">{meseLabel(mese)}</span> <span className="text-xs font-normal text-muted-foreground">— clicca un giorno per assegnare</span></h3>
      {mLoading ? <p className="text-muted-foreground">Caricamento…</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
          {giorni.map((g) => {
            const vuoto = g.totale === 0;
            const complete = !vuoto && g.da_assegnare === 0;
            return (
              <button key={g.data} onClick={() => !vuoto && setOpenDay(g.data)} disabled={vuoto} data-testid={`pul-pian-day-${g.data}`}
                className={`text-left bg-white rounded-xl border p-3 transition-shadow ${vuoto ? "opacity-50 cursor-default" : "hover:shadow-md cursor-pointer"} ${g.data === todayIso ? "ring-2 ring-sky-400" : ""} ${complete ? "border-emerald-200" : g.da_assegnare > 0 ? "border-amber-200" : ""}`}>
                <div className="flex items-baseline justify-between">
                  <div className="font-head font-bold text-lg leading-none">{g.giorno}<span className="text-[10px] font-semibold text-muted-foreground ml-1">{DOW_ABBR[g.dow]}</span></div>
                  {!vuoto && (complete ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">completo</span> : <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{g.da_assegnare} da ass.</span>)}
                </div>
                {vuoto ? <div className="text-[11px] text-muted-foreground mt-2">Nessuna pulizia</div> : (
                  <div className="text-[11px] text-muted-foreground mt-2">{g.totale} pulizie · <span className="text-emerald-700 font-semibold">{g.assegnate} assegnate</span></div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AssegnaDialog({ dlg, structureId, operatori, onClose, onSaved }) {
  const [turno, setTurno] = useState("entrambi");
  const [mode, setMode] = useState("auto");
  const [opId, setOpId] = useState(operatori[0]?.id || "");
  const go = async () => {
    const op = operatori.find((o) => o.id === opId);
    const payload = { structure_id: structureId, turno, mode, operatore_id: mode === "operatore" ? opId : null, operatore_nome: mode === "operatore" && op ? `${op.nome} ${op.cognome}`.trim() : null };
    try {
      if (dlg.scope === "settimana") { const r = await api.post("/pulizie/assegna-settimana", { ...payload, dal: dlg.data }); toast.success(`${r.data.assegnate} pulizie assegnate nella settimana`); }
      else { const r = await api.post("/pulizie/assegna-giorno", { ...payload, data: dlg.data }); toast.success(`${r.data.assegnate} pulizie assegnate a ${r.data.operatori} operatori`); }
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="pul-assegna-dialog">
        <DialogHeader><DialogTitle>Assegna pulizie · {dlg.scope === "settimana" ? "intera settimana" : new Date(dlg.data).toLocaleDateString("it-IT")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold mb-1 block">Turno di riferimento</Label>
            <Select value={turno} onValueChange={setTurno}><SelectTrigger data-testid="pul-ass-turno"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="mattina">Mattina</SelectItem><SelectItem value="pomeriggio">Pomeriggio</SelectItem><SelectItem value="entrambi">Mattina + Pomeriggio</SelectItem></SelectContent></Select>
          </div>
          <div><Label className="text-xs font-semibold mb-1 block">Modalità</Label>
            <Select value={mode} onValueChange={setMode}><SelectTrigger data-testid="pul-ass-mode"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="auto">Automatica: distribuisci equamente agli OSS in turno</SelectItem><SelectItem value="operatore">Manuale: tutte a un operatore</SelectItem></SelectContent></Select>
          </div>
          {mode === "operatore" && <div><Label className="text-xs font-semibold mb-1 block">Operatore</Label>
            <Select value={opId} onValueChange={setOpId}><SelectTrigger data-testid="pul-ass-op"><SelectValue /></SelectTrigger><SelectContent>{operatori.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome} {o.cognome}</SelectItem>)}</SelectContent></Select>
          </div>}
          <p className="text-[11px] text-muted-foreground">In automatica, le pulizie del/i giorno/i vengono divise a rotazione tra gli OSS che risultano in turno.</p>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button><Button onClick={go} data-testid="pul-ass-go">Assegna</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
